import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'components/Modal';
import Button from 'components/UI/Button';
import Switch from 'components/UI/Switch';
import './styles.scss';

/**
 * Componente modular para validación de documentos
 * Permite mostrar una lista de documentos con switches que deben ser activados
 * antes de continuar con el proceso
 *
 * @param {Array} documents - Lista de documentos a validar
 * @param {string} documents[].id - Identificador único del documento
 * @param {string} documents[].label - Etiqueta/texto del documento
 * @param {string} title - Título del modal
 * @param {string} description - Descripción del modal
 * @param {string} continueText - Texto del botón continuar
 * @param {string} cancelText - Texto del botón cancelar
 * @param {boolean} isOpen - Controla si el modal está abierto
 * @param {Function} onContinue - Callback cuando se hace clic en continuar
 * @param {Function} onCancel - Callback cuando se hace clic en cancelar
 */
function DocumentValidationModal({
  documents = [],
  title = '',
  description = '',
  continueText = 'Continuar',
  cancelText = 'Cancelar',
  isOpen = false,
  onContinue,
  onCancel,
}) {
  const [checkedDocuments, setCheckedDocuments] = useState({});

  // Inicializar todos los documentos como no marcados
  useEffect(() => {
    if (isOpen && documents.length > 0) {
      const initialChecked = {};
      documents.forEach((doc) => {
        initialChecked[doc.id] = false;
      });
      setCheckedDocuments(initialChecked);
    }
  }, [isOpen, documents]);

  const handleToggleDocument = (documentId) => {
    setCheckedDocuments((prev) => ({
      ...prev,
      [documentId]: !prev[documentId],
    }));
  };

  const allDocumentsChecked = documents.every(
    (doc) => checkedDocuments[doc.id] === true,
  );

  const handleContinue = () => {
    if (allDocumentsChecked && onContinue) {
      onContinue();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      title={title}
      onClose={handleCancel}
      closeOnBackgroundClick={false}
      className='document-validation-modal'
    >
      {description && (
        <p className='document-validation-modal__description mb-6'>
          {description}
        </p>
      )}

      <div className='document-validation-modal__documents'>
        {documents.map((document) => (
          <div
            key={document.id}
            className='document-validation-modal__item flex items-center justify-between gap-4 mb-4'
          >
            <div
              className='flex items-center gap-1'
              onClick={() => handleToggleDocument(document.id)}
            >
              <p className='mb-0'>{document.label}</p>
            </div>
            <Switch
              checked={checkedDocuments[document.id] || false}
              onChange={() => handleToggleDocument(document.id)}
            />
          </div>
        ))}
      </div>

      <div className='document-validation-modal__actions mt-6'>
        <Button
          text={continueText}
          onClick={handleContinue}
          disabled={!allDocumentsChecked}
          className='mb-4'
        />
        {onCancel && (
          <Button
            text={cancelText}
            type='secondary'
            onClick={handleCancel}
          />
        )}
      </div>
    </Modal>
  );
}

DocumentValidationModal.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  continueText: PropTypes.string,
  cancelText: PropTypes.string,
  isOpen: PropTypes.bool,
  onContinue: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default DocumentValidationModal;
